/**
 * ChampionshipOdds — Season Simulation (v2 redesign)
 *
 * Runs 10,000 simulated seasons to project each team's championship odds.
 * Layout: dark donut card (your title odds as an SVG ring) + odds table
 * with gradient bars; your row is tinted with a YOU tag.
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import ToolNav from '../components/ToolNav'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Page, V2Card, V2Icons, V2TableHead, BlueTag, JaiSummaryCard, SecondaryBtn } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'

// ── Preview flag ───────────────────────────────────────────────────────────────
const IS_PREVIEW =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('preview') === '1'

// ── Mock data ──────────────────────────────────────────────────────────────────
const PREVIEW_LEAGUE = {
  id: 'preview',
  name: 'The Hardwood Cartel',
  teamName: 'Rim Reapers',
  season: 2025,
}

const PREVIEW_TEAMS = [
  { id: 't1', name: 'Rim Reapers',     isMe: true,  champOdds: 34.2, playoffProb: 91.5, weekDelta: +6.8, record: '10-4', avgPts: 184.2 },
  { id: 't2', name: 'Hoop Schemers',   isMe: false, champOdds: 22.1, playoffProb: 78.3, weekDelta: -1.2, record: '7-7',  avgPts: 171.4 },
  { id: 't3', name: 'Dunks & Dimes',   isMe: false, champOdds: 18.7, playoffProb: 72.1, weekDelta: -3.5, record: '7-7',  avgPts: 169.8 },
  { id: 't4', name: 'Playoff or Bust', isMe: false, champOdds: 11.3, playoffProb: 54.2, weekDelta: +2.1, record: '6-8',  avgPts: 163.1 },
  { id: 't5', name: 'The Benchmarks',  isMe: false, champOdds: 7.4,  playoffProb: 41.8, weekDelta: +0.9, record: '5-9',  avgPts: 158.4 },
  { id: 't6', name: 'No Cap Squad',    isMe: false, champOdds: 3.8,  playoffProb: 28.5, weekDelta: -2.0, record: '5-9',  avgPts: 155.2 },
  { id: 't7', name: 'Brick City FC',   isMe: false, champOdds: 1.9,  playoffProb: 16.7, weekDelta: -4.1, record: '4-10', avgPts: 148.7 },
  { id: 't8', name: 'Foul Trouble',    isMe: false, champOdds: 0.6,  playoffProb: 7.3,  weekDelta: -0.8, record: '3-11', avgPts: 141.5 },
]

const JAI_INSIGHT = "Rim Reapers have the highest championship probability in the simulation, driven by a 5-game win streak and the best points-per-week average in the league. The primary risk is a soft schedule that flatters the numbers — Rim Reapers have not yet faced the top three teams head-to-head this season. JAI projects a 91.5% playoff lock with the title genuinely up for grabs."

function ordinalWord(n) {
  const words = ['BEST', '2ND BEST', '3RD BEST']
  return words[n] || `${n + 1}TH BEST`
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function ChampionshipOdds() {
  const { id } = useParams()
  const [league, setLeague] = useState(IS_PREVIEW ? PREVIEW_LEAGUE : null)
  const [loading, setLoading] = useState(!IS_PREVIEW)
  const [simRunning, setSimRunning] = useState(false)

  useEffect(() => {
    if (IS_PREVIEW) return
    leaguesApi.get(id).then(r => {
      setLeague(r.data.league)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [id])

  const reSimulate = () => {
    setSimRunning(true)
    setTimeout(() => setSimRunning(false), 1800)
  }

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <span className="text-sm" style={{ color: V2.faint }}>Loading…</span>
        </div>
      </AppShell>
    )
  }

  const teams   = [...PREVIEW_TEAMS].sort((a, b) => b.champOdds - a.champOdds)
  const meIndex = teams.findIndex(t => t.isMe)
  const me      = teams[meIndex]
  const maxOdds = teams[0]?.champOdds || 1

  // Donut geometry
  const r = 54
  const c = 2 * Math.PI * r
  const grid = '40px 1fr 70px 1fr 70px'

  return (
    <AppShell>
      <V2Page
        topBar={<ToolNav leagueId={id} />}
        kicker={
          <Link to={`/league/${id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            {league?.name ? `${league.name} · 10,000 season simulations` : '10,000 season simulations'}
          </Link>
        }
        title="Championship Odds"
        sub="JAI re-simulates the rest of the season after every matchup."
        maxWidth={880}
        action={
          <SecondaryBtn onClick={reSimulate} disabled={simRunning}>
            <span className={simRunning ? 'animate-spin inline-flex' : 'inline-flex'}>{V2Icons.refresh}</span>
            {simRunning ? 'Simulating…' : 'Re-simulate'}
          </SecondaryBtn>
        }
      >

        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16, alignItems: 'start', marginBottom: 16 }}>

          {/* ── Dark donut card ────────────────────────────────────────── */}
          {me && (
            <div style={{ background: V2.darkHero, borderRadius: 16, padding: '24px 20px', color: 'white', textAlign: 'center' }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.45)', fontFamily: JAI_FONTS.mono, marginBottom: 16 }}>
                YOUR TITLE ODDS
              </div>
              <div style={{ position: 'relative', width: 140, height: 140, margin: '0 auto' }}>
                <svg width="140" height="140" viewBox="0 0 140 140">
                  <circle cx="70" cy="70" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="11" />
                  <circle
                    cx="70" cy="70" r={r} fill="none" stroke="url(#oddsGradient)" strokeWidth="11" strokeLinecap="round"
                    strokeDasharray={`${c * (me.champOdds / 100)} ${c}`} transform="rotate(-90 70 70)"
                  />
                  <defs>
                    <linearGradient id="oddsGradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0" stopColor="#1E5BD6" />
                      <stop offset="1" stopColor="#3A7CFF" />
                    </linearGradient>
                  </defs>
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 30, fontWeight: 800, fontFamily: JAI_FONTS.mono, color: V2.blueBright, letterSpacing: '-0.02em' }}>
                    {me.champOdds.toFixed(1)}%
                  </span>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontFamily: JAI_FONTS.mono, marginTop: 2 }}>
                    {ordinalWord(meIndex)}
                  </span>
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, marginTop: 16 }}>
                {me.weekDelta >= 0 ? 'Up ' : 'Down '}
                <strong style={{ color: me.weekDelta >= 0 ? V2.successOnDark : V2.errorOnDark }}>
                  {me.weekDelta >= 0 ? '+' : ''}{me.weekDelta.toFixed(1)}%
                </strong>{' '}
                since last week · {me.playoffProb.toFixed(1)}% playoff probability.
              </div>
            </div>
          )}

          {/* ── Odds table ─────────────────────────────────────────────── */}
          <V2Card pad="0">
            <V2TableHead grid={grid} cols={['#', 'Team', ['Record', 'right'], 'Title odds', ['Odds', 'right']]} />
            {teams.map((team, i) => (
              <div
                key={team.id}
                style={{
                  display: 'grid', gridTemplateColumns: grid, gap: 12, alignItems: 'center',
                  padding: '12px 20px',
                  borderBottom: i < teams.length - 1 ? '1px solid rgba(10,31,68,0.05)' : 'none',
                  background: 'white', boxShadow: team.isMe ? 'inset 3px 0 0 #1E5BD6' : 'none',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 800, color: team.isMe ? V2.blue : V2.faint, fontFamily: JAI_FONTS.mono }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: V2.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {team.name}
                  </span>
                  {team.isMe && <BlueTag>You</BlueTag>}
                </div>
                <span style={{ textAlign: 'right', fontSize: 12.5, color: V2.mute, fontFamily: JAI_FONTS.mono }}>{team.record}</span>
                <div style={{ height: 7, borderRadius: 999, background: 'rgba(10,31,68,0.06)', overflow: 'hidden' }}>
                  <div style={{
                    width: `${(team.champOdds / maxOdds) * 100}%`, height: '100%', borderRadius: 999,
                    background: team.isMe ? 'linear-gradient(90deg,#1E5BD6,#3A7CFF)' : 'rgba(30,91,214,0.35)',
                  }} />
                </div>
                <span style={{ textAlign: 'right', fontSize: 13.5, fontWeight: 800, color: team.isMe ? V2.blue : V2.navy, fontFamily: JAI_FONTS.mono }}>
                  {team.champOdds.toFixed(1)}%
                </span>
              </div>
            ))}
          </V2Card>
        </div>

        {/* JAI insight */}
        <JaiSummaryCard headline="The simulation's read on your title chances.">
          {JAI_INSIGHT}
        </JaiSummaryCard>

        <p style={{ textAlign: 'center', fontSize: 11.5, color: V2.faint, marginTop: 16 }}>
          Simulations account for remaining schedule, current roster strength, injury risk, and historical scoring variance.
        </p>

      </V2Page>
    </AppShell>
  )
}
